import {AutocompleteRenderInputParams} from '@mui/material';
import {hierarchyTreeModel, utils} from '@wallaby-ui/core';
import {isArray, map, sortBy} from 'lodash';
import React, {useCallback, useMemo, useRef} from 'react';
import {useIntl} from 'react-intl';
import {DynamicFilter, Filter, FILTER_TYPE_MULTI_SELECT} from 'src/js/lib/filtering';
import {FilterNode as FilterNodeModel, FilterSchema} from 'src/js/model/filters_config';

import {STATIC_EMPTY_ARRAY} from './FilterNodeContainer';
import FilterNodeSelectRemoveAll from './FilterNodeSelectRemoveAll';
import FilterNodeSelectRemoveItem from './FilterNodeSelectRemoveItem';
import {FilterNodeTypeHierarchySyncMultiSelect} from './FilterNodeTypeHierarchySyncMultiSelect';
import {I18N} from './FilterNodeTypeSelectContainer';
import {FilterNodeTypeWrapper} from './FilterNodeTypeWrapper';
import {getSelectOptionIdProperty} from '../filtering_helpers';

export interface FilterNodeTypeHierarchySyncMultiSelectContainerProps {
  inputRef: (inputElement: HTMLInputElement) => void;
  setFocus: () => void;
  node: FilterNodeModel;
  changeFilter: (values: any) => void;
  multi?: boolean;
  removeFilter: (filter: Filter) => void;
  schema: FilterSchema;
  selectedFilter?: Filter;
  toggleOpened: () => void;
  options: any;
  valueProperty: string;
  isMui?: boolean;
  isOpened?: boolean;
  hierarchyData: hierarchyTreeModel.HierarchyNodeFlat[];
  getOptionDisabled?: (row: any) => boolean;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  marginBlock?: string;
  maxWidth?: string | number;
}

type Props = FilterNodeTypeHierarchySyncMultiSelectContainerProps;

/** @deprecated use `<LightHierarchyFilter>` instead */
export const FilterNodeTypeHierarchySyncMultiSelectContainer: React.FC<Props> = (props) => {
  const {
    changeFilter,
    inputRef,
    isOpened = false,
    multi = false,
    node,
    options,
    toggleOpened,
    removeFilter,
    selectedFilter,
    setFocus,
    isMui,
    schema,
    ...restProps
  } = props;

  const intl = useIntl();
  const dlSelectComponents = useMemo(() => {
    return {
      ClearIndicator: FilterNodeSelectRemoveAll,
      MultiValueRemove: FilterNodeSelectRemoveItem,
    };
  }, []);

  const idProperty = getSelectOptionIdProperty(isMui);

  const getFilter = useCallback(
    (value) => {
      const MultiSelectFilter = DynamicFilter.getFilterClass(FILTER_TYPE_MULTI_SELECT);
      return new MultiSelectFilter({
        node,
        type: FILTER_TYPE_MULTI_SELECT,
        property: node.getName(),
        value,
      });
    },
    [node]
  );

  const changeSelectedFilter = useCallback(
    (ids) => {
      const filter = getFilter(ids);
      if (multi ? ids.length > 0 : ids) {
        changeFilter(filter);
      } else {
        removeFilter(filter);
      }
    },
    [getFilter, multi, changeFilter, removeFilter]
  );

  const handleChange = (value) => {
    if (multi) {
      const ids = map(value, (item) => item[idProperty]);
      changeSelectedFilter(ids);
    } else {
      const id = value?.[idProperty] ?? null;
      changeSelectedFilter(id);
    }
  };

  const getOptionsFromFilterConfig = () => {
    let additionalOptions;
    if (!options) {
      additionalOptions = node.getChildrenIds().map((childId) => {
        const filterNode = schema.getFilter(childId);
        const label = filterNode.getLabel();
        const translatedLabel =
          label && utils.TranslateUtils.hasTranslation(label, intl)
            ? utils.TranslateUtils.translate(label, intl)
            : label || filterNode.getName();
        return {
          [idProperty]: filterNode.getName(),
          label: translatedLabel,
        };
      });
    }
    return additionalOptions ? sortBy(additionalOptions, ['label']) : STATIC_EMPTY_ARRAY;
  };
  // NOTE: hierarchyNodeClick is cached later, so we need to keep a ref here
  const currentValue = useRef(selectedFilter?.getValue() ?? []);
  currentValue.current = selectedFilter?.getValue() ?? [];
  const hierarchyNodeClick = useCallback(
    (node: hierarchyTreeModel.HierarchyTreeNode) => {
      if (multi) {
        const prevValue = currentValue.current;
        const valueToAdd = String(node.getId());
        if (prevValue.indexOf(valueToAdd) === -1) {
          // add if it is not already there
          const values = [...(isArray(prevValue) ? prevValue : [prevValue]), node.getId() && String(node.getId())];
          changeSelectedFilter(values);
        }
      } else {
        const addedValue = String(node.getId());
        changeSelectedFilter(addedValue);
      }
    },
    [changeSelectedFilter, multi]
  );

  const value: string[] | string | null = selectedFilter?.getValue() ?? null;
  const filter = getFilter(value);
  let highlightedValues: string[] = [];
  if (multi) {
    highlightedValues = (value ?? []) as string[];
  } else if (value) {
    highlightedValues = [value as string];
  }

  return (
    <FilterNodeTypeWrapper
      filter={filter}
      isOpened={isOpened} // TODO: is this "isOpened" ever used?
      node={node}
      removeFilter={removeFilter}
      setFocus={setFocus}
      selectedFilter={selectedFilter}
      toggleOpened={toggleOpened}
      isMui={isMui}
    >
      <FilterNodeTypeHierarchySyncMultiSelect
        isOpened={isOpened}
        inputRef={inputRef}
        name={node.getName()}
        id={node.getName()}
        node={node}
        label={node.getLabel()}
        selectedValue={value}
        isMulti={multi}
        isClearable={!node.isNotRemovable()}
        noOptionsText={I18N.NO_OPTIONS.id}
        placeholderText={node.getPlaceholder() ?? I18N.PLACEHOLDER.id}
        tabIndex={isOpened ? 0 : -1}
        onChange={handleChange}
        options={options ?? getOptionsFromFilterConfig()}
        components={dlSelectComponents}
        isMui={isMui}
        hierarchyNodeClick={hierarchyNodeClick}
        highlightedValues={highlightedValues}
        labelProperty='label'
        {...restProps}
      />
    </FilterNodeTypeWrapper>
  );
};
